import { create } from 'zustand';
import { abortUpload, uploadFile, UploadAbortedError, type UploadMeta } from '@/lib/upload/multipart';
import { defaultLabel, guessKind, isMediaFile } from '@/lib/upload/plan';
import { logError } from '@/lib/log';
import type { SourceKind } from '@/lib/api/types';

/**
 * Upload queue for episode media. Lives outside React so uploads keep running while the user moves around the app.
 * Files upload one at a time (each with 4 parallel parts), which keeps the studio uplink saturated without
 * starving the API calls of the rest of the app.
 */
export type UploadStatus = 'ready' | 'queued' | 'uploading' | 'paused' | 'done' | 'failed';

export interface UploadItem {
  id: string;
  sessionId: string;
  file: File;
  kind: SourceKind;
  person: string;
  label: string;
  /** "People in frame" as typed (comma-separated); sent as `people[]` for cameras (amendment 11). */
  people: string;
  status: UploadStatus;
  bytes: number;
  total: number;
  /** bytes per second, smoothed */
  speed: number;
  resumedFrom: number | null;
  sourceId: string | null;
  error: string | null;
}

interface UploadsState {
  items: UploadItem[];
  add: (sessionId: string, files: File[]) => number;
  update: (id: string, patch: Partial<Pick<UploadItem, 'kind' | 'person' | 'label' | 'people'>>) => void;
  remove: (id: string) => void;
  startAll: (sessionId: string) => void;
  pause: (id: string) => void;
  resume: (id: string) => void;
  clearDone: (sessionId: string) => void;
}

/** "Noa, Ido ,, Dana" → ["Noa", "Ido", "Dana"]: trimmed, de-duplicated, at most 10 names of ≤ 120 characters. */
export function parsePeople(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[,،\n]/)) {
    const name = raw.trim().replace(/\s+/g, ' ').slice(0, 120);
    if (name && !out.some((n) => n.toLowerCase() === name.toLowerCase())) out.push(name);
  }
  return out.slice(0, 10);
}

/** Body fields for POST /sessions/{id}/uploads. `people` is sent for cameras only, and only when names were typed. */
export function uploadMeta(item: Pick<UploadItem, 'kind' | 'person' | 'label' | 'people'>): UploadMeta {
  const people = item.kind === 'camera' ? parsePeople(item.people) : [];
  return { kind: item.kind, person: item.person, label: item.label, ...(people.length ? { people } : {}) };
}

const controllers = new Map<string, AbortController>();
let running = false;
let seq = 0;

const patch = (id: string, p: Partial<UploadItem>) =>
  useUploads.setState((s) => ({ items: s.items.map((i) => (i.id === id ? { ...i, ...p } : i)) }));

async function pump(): Promise<void> {
  if (running) return;
  running = true;
  try {
    for (;;) {
      const next = useUploads.getState().items.find((i) => i.status === 'queued');
      if (!next) break;
      await runOne(next);
    }
  } finally {
    running = false;
  }
}

async function runOne(item: UploadItem): Promise<void> {
  const ctl = new AbortController();
  controllers.set(item.id, ctl);
  patch(item.id, { status: 'uploading', error: null });
  let last = { bytes: item.bytes, at: performance.now() };
  const meta = uploadMeta(item);
  try {
    const { source_id } = await uploadFile(item.sessionId, item.file, meta, {
      signal: ctl.signal,
      onResume: (firstPart) => patch(item.id, { resumedFrom: firstPart }),
      onStarted: (sourceId) => patch(item.id, { sourceId }),
      onProgress: (bytes, total) => {
        const now = performance.now();
        const dt = (now - last.at) / 1000;
        if (dt >= 0.5) {
          const inst = Math.max(0, (bytes - last.bytes) / dt);
          const prev = useUploads.getState().items.find((i) => i.id === item.id)?.speed ?? 0;
          patch(item.id, { bytes, total, speed: prev ? prev * 0.7 + inst * 0.3 : inst });
          last = { bytes, at: now };
        } else {
          patch(item.id, { bytes, total });
        }
      },
    });
    patch(item.id, { status: 'done', sourceId: source_id, bytes: item.file.size, speed: 0 });
  } catch (e) {
    if (e instanceof UploadAbortedError || (e as Error).name === 'AbortError') {
      patch(item.id, { status: 'paused', speed: 0 });
    } else {
      logError('upload', `session=${item.sessionId} file="${item.file.name}" failed`, e);
      patch(item.id, { status: 'failed', speed: 0, error: (e as Error).message || 'Upload failed' });
    }
  } finally {
    controllers.delete(item.id);
  }
}

export const useUploads = create<UploadsState>()((set, get) => ({
  items: [],
  add: (sessionId, files) => {
    const media = files.filter(isMediaFile);
    const fresh = media
      .filter((f) => !get().items.some((i) => i.sessionId === sessionId && i.file.name === f.name && i.file.size === f.size && i.status !== 'done'))
      .map<UploadItem>((file) => ({
        id: `u${++seq}`,
        sessionId,
        file,
        kind: guessKind(file),
        person: '',
        label: defaultLabel(file.name),
        people: '',
        status: 'ready',
        bytes: 0,
        total: file.size,
        speed: 0,
        resumedFrom: null,
        sourceId: null,
        error: null,
      }));
    set((s) => ({ items: [...s.items, ...fresh] }));
    return fresh.length;
  },
  update: (id, p) => patch(id, p),
  remove: (id) => {
    const item = get().items.find((i) => i.id === id);
    controllers.get(id)?.abort();
    set((s) => ({ items: s.items.filter((i) => i.id !== id) }));
    // A started but unfinished upload is cancelled on the server so it doesn't block processing.
    if (item?.sourceId && item.status !== 'done') void abortUpload(item.sessionId, item.file, item.sourceId);
  },
  startAll: (sessionId) => {
    set((s) => ({ items: s.items.map((i) => (i.sessionId === sessionId && (i.status === 'ready' || i.status === 'failed') ? { ...i, status: 'queued', error: null } : i)) }));
    void pump();
  },
  pause: (id) => {
    const item = get().items.find((i) => i.id === id);
    if (item?.status === 'queued') patch(id, { status: 'paused' });
    controllers.get(id)?.abort();
  },
  resume: (id) => {
    patch(id, { status: 'queued', error: null });
    void pump();
  },
  clearDone: (sessionId) => set((s) => ({ items: s.items.filter((i) => !(i.sessionId === sessionId && i.status === 'done')) })),
}));

export function hasActiveUploads(): boolean {
  return useUploads.getState().items.some((i) => i.status === 'uploading' || i.status === 'queued');
}
